import {
  Children,
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

// Canonical surface (4 variants × 3 sizes, 8px radius). All redesigned
// surfaces should use these. The legacy values below are accepted for
// backward-compatibility with non-redesigned callers and resolve to one
// of the canonical variants/sizes.
type ButtonVariant =
  | "link"
  | "primary"
  | "secondary"
  | "outline"
  // legacy aliases
  | "ghost"
  | "accent"
  | "danger";

type ButtonSize =
  | "sm"
  | "md"
  | "lg"
  // legacy aliases
  | "xs"
  | "xl"
  | "auto";

// Legacy prop kept so existing callers compile. Always 8px radius now —
// the value is ignored.
type ButtonShape = "rounded" | "soft" | "pill" | "round" | "square";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  isLoading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

export type ButtonClassOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  iconOnly?: boolean;
  className?: string;
};

const variantClasses: Record<ButtonVariant, string> = {
  link: "bg-transparent text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
  ghost:
    "bg-transparent text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
  primary:
    "bg-[color:var(--primary-500)] text-[color:var(--on-accent,#fff)] border border-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] hover:border-[color:var(--primary-600)]",
  secondary:
    "bg-[color:var(--accent-500)] text-[color:var(--on-accent,var(--primary-500))] border border-[color:var(--accent-500)] hover:bg-[color:var(--accent-600)] hover:border-[color:var(--accent-600)]",
  accent:
    "bg-[color:var(--accent-500)] text-[color:var(--on-accent,var(--primary-500))] border border-[color:var(--accent-500)] hover:bg-[color:var(--accent-600)] hover:border-[color:var(--accent-600)]",
  outline:
    "bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]",
  danger:
    "bg-[color:var(--danger-500,var(--error-500))] text-white border border-[color:var(--danger-500,var(--error-500))] hover:bg-[color:var(--danger-600,var(--error-600))] hover:border-[color:var(--danger-600,var(--error-600))]",
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-[11.5px] gap-1",
  sm: "h-7 px-2.5 text-[11.5px] gap-1",
  md: "h-[34px] px-3.5 text-[13px] gap-1.5",
  lg: "h-10 px-[18px] text-sm gap-2",
  xl: "h-10 px-[18px] text-sm gap-2",
  auto: "gap-2",
};

const iconOnlySizeClasses: Record<ButtonSize, string> = {
  xs: "h-7 w-7 p-0 text-[11.5px]",
  sm: "h-7 w-7 p-0 text-[11.5px]",
  md: "h-[34px] w-[34px] p-0 text-[13px]",
  lg: "h-10 w-10 p-0 text-sm",
  xl: "h-10 w-10 p-0 text-sm",
  auto: "p-0",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export function buttonClass({
  variant = "link",
  size = "md",
  fullWidth = false,
  iconOnly = false,
  className,
}: ButtonClassOptions = {}) {
  return cx(
    "inline-flex items-center justify-center font-semibold rounded-[8px] transition-colors select-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
    "disabled:opacity-[0.55] disabled:cursor-not-allowed",
    variantClasses[variant],
    iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
    fullWidth && "w-full",
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      variant = "link",
      size = "md",
      // shape is accepted for legacy callers but ignored (radius is 8px)
      shape: _shape,
      fullWidth = false,
      isLoading = false,
      disabled,
      leadingIcon,
      trailingIcon,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = Boolean(disabled || isLoading);
    const hasChildren = Children.count(children) > 0;
    const iconOnly = !hasChildren && Boolean(leadingIcon || trailingIcon);

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        className={buttonClass({
          variant,
          size,
          fullWidth,
          iconOnly,
          className,
        })}
        {...props}
      >
        {isLoading ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            <Spinner />
          </span>
        ) : leadingIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {leadingIcon}
          </span>
        ) : null}
        {hasChildren ? (
          <span className="whitespace-nowrap">{children}</span>
        ) : null}
        {trailingIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {trailingIcon}
          </span>
        ) : null}
      </button>
    );
  },
);

Button.displayName = "Button";

// Inline loading spinner. Sized to the current font (1em) so it matches the
// button's text/icon slot at every size, and inherits the text color via
// currentColor. Honors prefers-reduced-motion by dropping the spin.
function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
      className="motion-safe:animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
